<script setup lang="ts">
import { Input } from '@/ui/input'
import AppTooltip from '@/components/ui/AppTooltip.vue'

interface InputTip {
  text: string
  link?: {
    value: string
    isOpenedInNewTab?: boolean
  }
}

defineOptions({
  inheritAttrs: false
})

const props = withDefaults(
  defineProps<{
    errorClasses?: string
    errors?: string[]
    inputClasses?: string
    fullWidth?: boolean
    id: string
    label?: string
    modelValue?: string | number | boolean
    tip?: InputTip
    toolTip?: string
    type?: string
    min?: number | string | null
    max?: number | string | null
  }>(),
  {
    errorClasses: null,
    errors: () => [],
    inputClasses: null,
    fullWidth: false,
    label: null,
    modelValue: undefined,
    tip: null,
    toolTip: null,
    type: 'text',
    min: null,
    max: null
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number): void
  (e: 'onFocus'): void
  (e: 'onBlur'): void
}>()

const slots = useSlots()
const hasIconLeft = computed(() => !!slots['icon-left'])
const hasIconRight = computed(() => !!slots['icon-right'])

const inputValue = computed(() => (typeof props.modelValue === 'boolean' ? String(props.modelValue) : props.modelValue))

const hasError = computed(() => props.errors?.length > 0)

const isNumericType = computed(() => ['numeric', 'decimal', 'number'].includes(props.type))

const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  let value: string | number = target.value

  if (isNumericType.value && value !== '') {
    value = Number(value)
  }

  emit('update:modelValue', value)
}

const rootRef = ref<HTMLElement | null>(null)

const focus = () => {
  nextTick(() => {
    rootRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    rootRef.value?.querySelector<HTMLInputElement>('[data-slot="input"]')?.focus()
  })
}

defineExpose({ focus })

const onBlur = (event: Event) => {
  const target = event.target as HTMLInputElement
  let value: string | number = target.value

  if (isNumericType.value && value !== '') {
    const num = Number(value)
    let clamped = num

    const minNum = props.min != null ? Number(props.min) : null
    const maxNum = props.max != null ? Number(props.max) : null

    if (minNum != null && num < minNum) clamped = minNum
    if (maxNum != null && num > maxNum) clamped = maxNum

    if (clamped !== num) {
      target.value = String(clamped)
      emit('update:modelValue', clamped)
    }
  }

  emit('onBlur')
}
</script>

<template>
  <div ref="rootRef" :class="[fullWidth ? 'w-full' : '', $attrs.class]">
    <div v-if="label || toolTip" class="mb-1.5 flex w-full items-center text-left text-sm font-normal text-foreground">
      <label v-if="label" :for="id">{{ label }}<sup v-if="$attrs.required" class="ml-1 text-red-600">*</sup></label>
      <AppTooltip v-if="toolTip" tooltip-position="top-start" :text="toolTip">
        <BaseIcon name="IconoirInfoCircle" class="ml-1.5 h-4 w-4 cursor-pointer text-foreground-secondary" />
      </AppTooltip>
    </div>
    <div class="relative flex w-full">
      <span
        v-if="hasIconLeft"
        class="inline-flex items-center rounded-l-control border border-r-0 border-input bg-muted px-3"
      >
        <slot name="icon-left" />
      </span>
      <Input
        :id="id"
        :type="type"
        :model-value="inputValue"
        :min="min"
        :max="max"
        v-bind="{ ...$attrs, class: undefined }"
        :aria-invalid="hasError || undefined"
        :class="[
          'h-10 sm:text-sm',
          hasIconLeft && !hasIconRight && 'rounded-l-none',
          hasIconRight && !hasIconLeft && 'rounded-r-none',
          hasIconLeft && hasIconRight && 'rounded-none',
          inputClasses
        ]"
        @input="onInput"
        @focus="emit('onFocus')"
        @blur="onBlur"
      />
      <span
        v-if="hasIconRight"
        :class="[
          'inline-flex items-center rounded-r-control border border-l-0 px-3',
          $attrs.disabled ? 'bg-muted border-input' : 'border-input bg-muted'
        ]"
      >
        <slot name="icon-right" />
      </span>
    </div>
    <div v-if="tip" class="mt-1.5 text-sm font-normal">
      <a
        :class="tip.link ? 'cursor-pointer text-blue-600 hover:text-blue-700 hover:underline' : 'text-muted-foreground'"
        :href="tip.link?.value"
        :target="tip.link?.isOpenedInNewTab ? '_blank' : '_self'"
      >
        {{ tip.text }}
      </a>
    </div>
    <slot name="subtext" />
    <p v-if="hasError" id="headline-error" :class="['mt-2 text-sm text-red-600', errorClasses]">
      {{ errors[0] }}
    </p>
  </div>
</template>
